import type { Persona } from "../Persona";
import { ModelName } from "../ModelName";

/**
 * Personas that are switched off and no longer appear in the selector.
 *
 * They stay part of `AVAILABLE_PERSONAS` rather than being deleted so that
 * threads created while a persona was still active continue to resolve through
 * `getPersonaById` — otherwise those threads would silently fall back to the
 * default persona and lose their original model and system message.
 */
export const INACTIVE_PERSONAS: Persona[] = [
  {
    id: 0,
    name: "ChatGPT 3.5",
    modelName: ModelName.GPT_35_TURBO,
    imageCapable: false,
    active: false,
  },
  {
    id: 1,
    name: "ChatGPT 4",
    modelName: ModelName.GPT_4_TURBO,
    imageCapable: true,
    active: false,
  },
  {
    id: 2,
    name: "Corteva Benefits",
    modelName: ModelName.GPT_41_MINI,
    imageCapable: false,
    active: false,
    description: `Ask questions about your Corteva benefits.`,
    welcomeMessage: `**Corteva Chat Benefits** is an experimental AI that can help answer questions about your Corteva benefits.
All information is sourced from the public [Corteva Benefits Website](https://hrportal.ehr.com/corteva/).
> Answers should be verified with the source links provided.`,
    examplePrompts: [
      "Compare my dental plan options",
      "What hollidays are paid time off?",
      "Explain the Retirement Savings Plan",
    ],
    isInternal: true,
    maturity: "preview",
  },
  {
    id: 3,
    name: "GMDS",
    modelName: ModelName.GPT_41_MINI,
    imageCapable: false,
    active: false,
    description:
      "Genome & Molecular Data Science onboarding and best practices.",
    welcomeMessage: `**Corteva Chat GMDS** is an experimental AI that can help answer questions related to Genome & Molecular Data Science.
All information is sourced from the [team docs website](https://gmds1.pages.dsigitlab.research.auto.pioneer.com/team-docs/).
> Answers should be verified with the source links provided.`,
  },
  {
    id: 4,
    name: "Data Science Playbook",
    modelName: ModelName.GPT_41_MINI,
    imageCapable: false,
    active: false,
    description: `Ask questions about Data Science standards and best practices.`,
    welcomeMessage: `**Corteva Chat Data Science Playbook** is an experimental AI that can help answer questions about Data Science standards and best practices.
All information is sourced from the [Data Science Playbook Website](https://data-science.pages.dsigitlab.research.auto.pioneer.com/ds-playbook/).
> Answers should be verified with the source links provided.`,
  },
  {
    id: 6,
    name: "ChatGPT 4o",
    modelName: ModelName.GPT_4O,
    imageCapable: true,
    active: false,
    description: "OpenAI's mainstream model, a good choice for common tasks.",
  },
  {
    id: 7,
    name: "Gemini 1.5 Pro",
    modelName: ModelName.GEMINI_1_5_PRO_002,
    imageCapable: true,
    active: false,
    description: "Googlg's high performance model for general tasks.",
  },
  {
    id: 8,
    name: "ChatGPT 4o Mini",
    modelName: ModelName.GPT_4O_MINI,
    imageCapable: true,
    active: false,
    description:
      "A faster and more affordable version of OpenAI's ChatGPT 4o model.",
  },
  {
    id: 9,
    name: "Best Practices for Product Chemistry and Identity Changes - Beta",
    modelName: ModelName.GPT_4O_MINI,
    imageCapable: false,
    active: false,
  },
  {
    id: 10,
    name: "OpenAI o1",
    modelName: ModelName.O1,
    imageCapable: false,
    active: false,
    description:
      "Trained with reinforcement learning to perform complex reasoning.",
  },
  {
    id: 11,
    name: "OpenAI o3 mini",
    modelName: ModelName.O3_MINI,
    imageCapable: false,
    active: false,
    description:
      "OpenAI's most recent reasoning model, high intelligence at a low cost.",
  },
  {
    id: 12,
    name: "Gemini 2.0 Flash",
    modelName: ModelName.GEMINI_2_0_FLASH,
    imageCapable: true,
    active: false,
    description: "Cost efficiency and low latency.",
  },
  {
    id: 13,
    name: "GPT 4.1",
    modelName: ModelName.GPT_41,
    imageCapable: true,
    active: false,
    description: "OpenAI's fourth generation model for complex tasks.",
  },
  {
    id: 14,
    name: "ChatGPT 4.1 Mini",
    modelName: ModelName.GPT_41_MINI,
    imageCapable: true,
    active: false,
    description: "Balanced for intelligence, speed, and cost.",
  },
  {
    id: 15,
    name: "ChatGPT 4.1 Nano",
    modelName: ModelName.GPT_41_NANO,
    imageCapable: true,
    active: false,
    description: "Fastest, most cost-effective GPT-4.1 model.",
  },
  {
    id: 16,
    name: "OpenAI o3",
    modelName: ModelName.O3,
    imageCapable: true,
    active: false,
    description: "OpenAI's most powerful reasoning model.",
  },
  {
    id: 17,
    name: "OpenAI o4 mini",
    modelName: ModelName.O4_MINI,
    imageCapable: true,
    active: false,
    description: "OpenAI's faster, more affordable reasoning model.",
  },
  {
    id: 18,
    name: "Gemini 2.5 Pro",
    modelName: ModelName.GEMINI_2_5_PRO,
    imageCapable: true,
    active: false,
    description:
      "Enhanced thinking and reasoning, multimodal understanding, advanced coding, and more.",
  },
  {
    id: 19,
    name: "Gemini 2.5 Flash",
    modelName: ModelName.GEMINI_2_5_FLASH,
    imageCapable: true,
    active: false,
    description: "Adaptive thinking, speed, cost efficiency.",
  },
  {
    id: 22,
    name: "ChemCrow",
    modelName: ModelName.CHEMCROW,
    imageCapable: false,
    active: false,
    codeExecutionCapable: false,
    description:
      "Open source agent from University of Rochester for reasoning-intensive chemical tasks.",
    welcomeMessage: `ChemCrow is an open source package for the accurate solution of reasoning-intensive chemical tasks.
    It uses a collection of chemical tools including RDKit, paper-qa, as well as some relevant databases in chemistry, like Pubchem and chem-space.
    This is an experimental AI. Answers should be verified with a chemist or domain expert.`,
    examplePrompts: [
      "What is the SMILES string for 2,4-Dichlorophenoxyacetic acid?",
      "What is the g/mol for 2,4-Dichlorophenoxyacetic acid?",
    ],
    maturity: "preview",
  },
  {
    id: 5,
    name: "CARL",
    modelName: ModelName.GPT_4O_MINI,
    imageCapable: true,
    active: false,
    codeExecutionCapable: false,
    description:
      "CARL (Corteva Agronomic Resource Library) provides agronomic and product information.",
    isInternal: true,
    allowIncognito: false,
  },
];
